/**
 * Gerador de QR Code nativo (sem dependências), porta compacta do algoritmo de
 * referência (Nayuki, MIT). Suporta byte-mode em qualquer versão; escolhe a
 * menor versão que cabe. Usado para o link público do formulário IMGR.
 *
 * Validado contra o pacote `qrcode` (oráculo) em `qrcode.test.ts`.
 */

export type Matriz = readonly (readonly boolean[])[];

// Tabelas do nível M, índice = versão-1. Uint8Array (indexa como number sob
// noUncheckedIndexedAccess — mantém o algoritmo livre de asserções).
const ECC_CW_POR_BLOCO = Uint8Array.from([
  10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28,
  28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28,
]);
const NUM_BLOCOS_EC = Uint8Array.from([
  1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29,
  31, 33, 35, 37, 38, 40, 43, 45, 47, 49,
]);

const MIN_VERSION = 1;
const MAX_VERSION = 40;
const ECC_FORMAT_BITS = 0x00; // nível M → bits 00 (mapa interno usa 0)

/** Quantidade de módulos de dados (em bits) disponíveis na versão dada. */
function numDataModules(ver: number): number {
  let result = (16 * ver + 128) * ver + 64;
  if (ver >= 2) {
    const numAlign = Math.floor(ver / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (ver >= 7) {
      result -= 36;
    }
  }
  return result;
}

function numRawDataCodewords(ver: number): number {
  return Math.floor(numDataModules(ver) / 8);
}

function eccCwPorBloco(ver: number): number {
  return ECC_CW_POR_BLOCO[ver - 1] as number;
}
function numBlocosEc(ver: number): number {
  return NUM_BLOCOS_EC[ver - 1] as number;
}
function numEccCodewords(ver: number): number {
  return eccCwPorBloco(ver) * numBlocosEc(ver);
}

/** Capacidade em bytes (byte-mode) considerando o indicador de modo/contagem. */
function capacidadeBytes(ver: number): number {
  const dataCapacityBits = (numRawDataCodewords(ver) - numEccCodewords(ver)) * 8;
  const ccBits = ver <= 9 ? 8 : 16;
  return Math.floor((dataCapacityBits - 4 - ccBits) / 8);
}

/* ----------------------------- Reed-Solomon ----------------------------- */

function reedSolomonComputeDivisor(degree: number): Uint8Array {
  const result = new Uint8Array(degree);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < result.length; j++) {
      result[j] = reedSolomonMultiply(result[j] as number, root);
      if (j + 1 < result.length) {
        result[j] = (result[j] as number) ^ (result[j + 1] as number);
      }
    }
    root = reedSolomonMultiply(root, 0x02);
  }
  return result;
}

function reedSolomonComputeRemainder(data: readonly number[], divisor: Uint8Array): Uint8Array {
  const result = new Uint8Array(divisor.length);
  for (const b of data) {
    const factor = (b ^ (result[0] as number)) & 0xff;
    result.copyWithin(0, 1); // desloca à esquerda…
    result[result.length - 1] = 0; // …e zera o último (equivale a shift+push(0))
    for (let i = 0; i < divisor.length; i++) {
      result[i] = (result[i] as number) ^ reedSolomonMultiply(divisor[i] as number, factor);
    }
  }
  return result;
}

function reedSolomonMultiply(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

/* --------------------------- Codificação byte --------------------------- */

function escolherVersao(bytes: readonly number[]): number {
  for (let ver = MIN_VERSION; ver <= MAX_VERSION; ver++) {
    if (bytes.length <= capacidadeBytes(ver)) {
      return ver;
    }
  }
  throw new Error("Dados longos demais para um QR Code");
}

function montarCodewords(bytes: readonly number[], ver: number): number[] {
  const ccBits = ver <= 9 ? 8 : 16;
  const bits: number[] = [];
  const append = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) {
      bits.push((val >>> i) & 1);
    }
  };
  append(0b0100, 4); // modo byte
  append(bytes.length, ccBits);
  for (const b of bytes) {
    append(b, 8);
  }
  const capacityBits = (numRawDataCodewords(ver) - numEccCodewords(ver)) * 8;
  append(0, Math.min(4, capacityBits - bits.length)); // terminador
  while (bits.length % 8 !== 0) {
    bits.push(0);
  }
  const dataCodewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let v = 0;
    for (let j = 0; j < 8; j++) {
      v = (v << 1) | (bits[i + j] ?? 0);
    }
    dataCodewords.push(v);
  }
  for (let pad = 0xec; dataCodewords.length < capacityBits / 8; pad ^= 0xec ^ 0x11) {
    dataCodewords.push(pad);
  }
  return dataCodewords;
}

/** Intercala os blocos de dados+ECC conforme o padrão (versão, nível M). */
function intercalarBlocos(dataCodewords: readonly number[], ver: number): number[] {
  const numBlocks = numBlocosEc(ver);
  const blockEccLen = eccCwPorBloco(ver);
  const rawCodewords = numRawDataCodewords(ver);
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortBlockLen = Math.floor(rawCodewords / numBlocks);

  const blocks: number[][] = [];
  const divisor = reedSolomonComputeDivisor(blockEccLen);
  let k = 0;
  for (let i = 0; i < numBlocks; i++) {
    const datLen = shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1);
    const dat = dataCodewords.slice(k, k + datLen);
    k += datLen;
    const ecc = reedSolomonComputeRemainder(dat, divisor);
    if (i < numShortBlocks) {
      dat.push(0); // placeholder p/ alinhar comprimento
    }
    blocks.push(dat.concat(Array.from(ecc)));
  }

  const result: number[] = [];
  const tamBloco = (blocks[0] as number[]).length;
  for (let i = 0; i < tamBloco; i++) {
    for (let j = 0; j < blocks.length; j++) {
      // Pula o byte placeholder dos blocos curtos na coluna de dados.
      if (i !== shortBlockLen - blockEccLen || j >= numShortBlocks) {
        result.push((blocks[j] as number[])[i] as number);
      }
    }
  }
  return result;
}

/* ------------------------------- Matriz -------------------------------- */

class Tela {
  readonly size: number;
  readonly modules: boolean[][];
  private readonly isFunction: boolean[][];

  constructor(ver: number) {
    this.size = ver * 4 + 17;
    this.modules = Array.from({ length: this.size }, () => new Array(this.size).fill(false));
    this.isFunction = Array.from({ length: this.size }, () => new Array(this.size).fill(false));
  }

  set(x: number, y: number, dark: boolean) {
    (this.modules[y] as boolean[])[x] = dark;
    (this.isFunction[y] as boolean[])[x] = true;
  }

  funcao(x: number, y: number): boolean {
    return (this.isFunction[y] as boolean[])[x] as boolean;
  }
}

function desenharPadroesFuncionais(t: Tela, ver: number) {
  const size = t.size;
  // Faixas de tempo.
  for (let i = 0; i < size; i++) {
    t.set(6, i, i % 2 === 0);
    t.set(i, 6, i % 2 === 0);
  }
  // Localizadores nos 3 cantos.
  desenharFinder(t, 3, 3);
  desenharFinder(t, size - 4, 3);
  desenharFinder(t, 3, size - 4);

  // Alinhamentos.
  const pos = posicoesAlinhamento(ver);
  const n = pos.length;
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const skip = (i === 0 && j === 0) || (i === 0 && j === n - 1) || (i === n - 1 && j === 0);
      if (!skip) {
        desenharAlinhamento(t, pos[i] as number, pos[j] as number);
      }
    }
  }

  // Informação de formato e versão (módulos reservados; preenchidos depois).
  desenharFormato(t, 0);
  desenharVersao(t, ver);
}

function desenharFinder(t: Tela, cx: number, cy: number) {
  for (let dy = -4; dy <= 4; dy++) {
    for (let dx = -4; dx <= 4; dx++) {
      const dist = Math.max(Math.abs(dx), Math.abs(dy));
      const x = cx + dx;
      const y = cy + dy;
      if (x >= 0 && x < t.size && y >= 0 && y < t.size) {
        t.set(x, y, dist !== 2 && dist !== 4);
      }
    }
  }
}

function desenharAlinhamento(t: Tela, cx: number, cy: number) {
  for (let dy = -2; dy <= 2; dy++) {
    for (let dx = -2; dx <= 2; dx++) {
      t.set(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  }
}

function posicoesAlinhamento(ver: number): number[] {
  if (ver === 1) {
    return [];
  }
  const numAlign = Math.floor(ver / 7) + 2;
  const step = ver === 32 ? 26 : Math.ceil((ver * 4 + 4) / (numAlign * 2 - 2)) * 2;
  const result = [6];
  for (let p = ver * 4 + 10; result.length < numAlign; p -= step) {
    result.splice(1, 0, p);
  }
  return result;
}

function desenharFormato(t: Tela, mask: number) {
  const data = (ECC_FORMAT_BITS << 3) | mask; // 2 bits nível (M=0) + 3 bits máscara
  let rem = data;
  for (let i = 0; i < 10; i++) {
    rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  }
  const bits = ((data << 10) | rem) ^ 0x5412;
  for (let i = 0; i <= 5; i++) {
    t.set(8, i, ((bits >>> i) & 1) !== 0);
  }
  t.set(8, 7, ((bits >>> 6) & 1) !== 0);
  t.set(8, 8, ((bits >>> 7) & 1) !== 0);
  t.set(7, 8, ((bits >>> 8) & 1) !== 0);
  for (let i = 9; i < 15; i++) {
    t.set(14 - i, 8, ((bits >>> i) & 1) !== 0);
  }
  const size = t.size;
  for (let i = 0; i < 8; i++) {
    t.set(size - 1 - i, 8, ((bits >>> i) & 1) !== 0);
  }
  for (let i = 8; i < 15; i++) {
    t.set(8, size - 15 + i, ((bits >>> i) & 1) !== 0);
  }
  t.set(8, size - 8, true); // módulo escuro fixo
}

function desenharVersao(t: Tela, ver: number) {
  if (ver < 7) {
    return;
  }
  let rem = ver;
  for (let i = 0; i < 12; i++) {
    rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  }
  const bits = (ver << 12) | rem;
  const size = t.size;
  for (let i = 0; i < 18; i++) {
    const bit = ((bits >>> i) & 1) !== 0;
    const a = size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    t.set(a, b, bit);
    t.set(b, a, bit);
  }
}

/** Coordenadas (x,y) na ordem de colocação dos dados (zigue-zague, pula coluna 6). */
function* celulasZigzag(size: number): Generator<[number, number]> {
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) {
      right = 5; // pula a coluna de tempo (reatribui p/ o decremento seguinte saltar)
    }
    for (let vert = 0; vert < size; vert++) {
      const upward = ((right + 1) & 2) === 0;
      const y = upward ? size - 1 - vert : vert;
      yield [right, y];
      yield [right - 1, y];
    }
  }
}

function desenharCodewords(t: Tela, allCodewords: readonly number[]) {
  const totalBits = allCodewords.length * 8;
  let i = 0;
  for (const [x, y] of celulasZigzag(t.size)) {
    if (t.funcao(x, y) || i >= totalBits) {
      continue;
    }
    const byte = allCodewords[i >>> 3] ?? 0;
    (t.modules[y] as boolean[])[x] = ((byte >>> (7 - (i & 7))) & 1) !== 0;
    i++;
  }
}

function aplicarMascara(t: Tela, mask: number) {
  const m = t.modules;
  for (let y = 0; y < t.size; y++) {
    const row = m[y] as boolean[];
    for (let x = 0; x < t.size; x++) {
      if (t.funcao(x, y)) {
        continue;
      }
      let invert: boolean;
      switch (mask) {
        case 0:
          invert = (x + y) % 2 === 0;
          break;
        case 1:
          invert = y % 2 === 0;
          break;
        case 2:
          invert = x % 3 === 0;
          break;
        case 3:
          invert = (x + y) % 3 === 0;
          break;
        case 4:
          invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
          break;
        case 5:
          invert = ((x * y) % 2) + ((x * y) % 3) === 0;
          break;
        case 6:
          invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
          break;
        default:
          invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
          break;
      }
      if (invert) {
        row[x] = !row[x];
      }
    }
  }
}

/* ------------------------------ Penalidade ------------------------------ */

type Get = (x: number, y: number) => boolean;

/** Penalidade de runs ≥5 numa única sequência (linha ou coluna). */
function penalRun(leitura: (j: number) => boolean, size: number): number {
  let p = 0;
  let cor = leitura(0);
  let run = 1;
  for (let j = 1; j < size; j++) {
    const c = leitura(j);
    if (c === cor) {
      run++;
      if (run === 5) p += 3;
      else if (run > 5) p++;
    } else {
      cor = c;
      run = 1;
    }
  }
  return p;
}

/** Regra 1: runs ≥5 em todas as linhas e colunas. */
function regra1(get: Get, size: number): number {
  let p = 0;
  for (let i = 0; i < size; i++) {
    p += penalRun((j) => get(j, i), size);
    p += penalRun((j) => get(i, j), size);
  }
  return p;
}

/** Regra 2: blocos 2×2 da mesma cor. */
function regra2(get: Get, size: number): number {
  let p = 0;
  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const c = get(x, y);
      if (c === get(x + 1, y) && c === get(x, y + 1) && c === get(x + 1, y + 1)) {
        p += 3;
      }
    }
  }
  return p;
}

/** Regra 3: padrão localizador de 11 módulos, em linha e coluna. */
function regra3(get: Get, size: number): number {
  let p = 0;
  const janela = (fx: (i: number) => number, fy: (i: number) => number): boolean => {
    const buf: boolean[] = [];
    for (let i = 0; i < 11; i++) {
      buf.push(get(fx(i), fy(i)));
    }
    return casaFinder(buf);
  };
  for (let y = 0; y < size; y++) {
    for (let x = 0; x + 11 <= size; x++) {
      if (
        janela(
          (i) => x + i,
          () => y,
        )
      )
        p += 40;
    }
  }
  for (let x = 0; x < size; x++) {
    for (let y = 0; y + 11 <= size; y++) {
      if (
        janela(
          () => x,
          (i) => y + i,
        )
      )
        p += 40;
    }
  }
  return p;
}

/** Regra 4: desequilíbrio claro/escuro. */
function regra4(get: Get, size: number): number {
  let dark = 0;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (get(x, y)) dark++;
    }
  }
  const total = size * size;
  const k = Math.floor((Math.abs(dark * 20 - total * 10) / total - 1) / 0.5);
  return Math.max(0, k) * 10;
}

/** Penalidade de uma máscara (4 regras do padrão). Só afeta a ESCOLHA da máscara. */
function penalidade(t: Tela): number {
  const m = t.modules;
  const get: Get = (x, y) => (m[y] as boolean[])[x] as boolean;
  return regra1(get, t.size) + regra2(get, t.size) + regra3(get, t.size) + regra4(get, t.size);
}

const FINDER = [true, false, true, true, true, false, true, false, false, false, false];
/** Janela de 11 módulos casa o padrão localizador (direta ou invertida). */
function casaFinder(janela: readonly boolean[]): boolean {
  let direta = true;
  let invertida = true;
  for (let i = 0; i < 11; i++) {
    if (janela[i] !== FINDER[i]) direta = false;
    if (janela[i] !== FINDER[10 - i]) invertida = false;
  }
  return direta || invertida;
}

/* ------------------------------- Público ------------------------------- */

const utf8 = (texto: string): number[] => Array.from(new TextEncoder().encode(texto));

/** Monta a matriz final para uma máscara específica (0..7). */
function montarComMascara(allCodewords: readonly number[], ver: number, mask: number): Tela {
  const t = new Tela(ver);
  desenharPadroesFuncionais(t, ver);
  desenharCodewords(t, allCodewords);
  aplicarMascara(t, mask);
  desenharFormato(t, mask);
  return t;
}

/** Gera a matriz (booleano = módulo escuro). Máscara forçada (testes) ou ótima. */
export function gerarQr(texto: string, maskForcada?: number): Matriz {
  const bytes = utf8(texto);
  const ver = escolherVersao(bytes);
  const dataCodewords = montarCodewords(bytes, ver);
  const allCodewords = intercalarBlocos(dataCodewords, ver);

  if (maskForcada !== undefined) {
    return montarComMascara(allCodewords, ver, maskForcada).modules;
  }
  let melhor = montarComMascara(allCodewords, ver, 0);
  let melhorPen = penalidade(melhor);
  for (let mask = 1; mask < 8; mask++) {
    const t = montarComMascara(allCodewords, ver, mask);
    const pen = penalidade(t);
    if (pen < melhorPen) {
      melhorPen = pen;
      melhor = t;
    }
  }
  return melhor.modules;
}

/** Renderiza a matriz como string SVG (escala em módulos; quiet zone de 4). */
export function qrSvg(texto: string, opts: { tamanho?: number; quiet?: number } = {}): string {
  const m = gerarQr(texto);
  const quiet = opts.quiet ?? 4;
  const count = m.length + quiet * 2;
  const tam = opts.tamanho ?? count * 4;
  let caminho = "";
  for (let y = 0; y < m.length; y++) {
    const row = m[y] as readonly boolean[];
    for (let x = 0; x < m.length; x++) {
      if (row[x]) {
        caminho += `M${x + quiet} ${y + quiet}h1v1h-1z`;
      }
    }
  }
  const abre = `<svg xmlns="http://www.w3.org/2000/svg" width="${tam}" height="${tam}" viewBox="0 0 ${count} ${count}" shape-rendering="crispEdges">`;
  const fundo = `<rect width="${count}" height="${count}" fill="#ffffff"/>`;
  const path = `<path d="${caminho}" fill="#0f172a"/>`;
  return `${abre}${fundo}${path}</svg>`;
}

/** O mesmo QR como data-URI (para `<img src>`, sem dangerouslySetInnerHTML). */
export function qrDataUri(texto: string, opts: { tamanho?: number; quiet?: number } = {}): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(qrSvg(texto, opts))}`;
}
